import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { isYouTubeUrl } from '@/lib/youtube';

export interface VideoEvent extends NostrEvent {
  kind: 21 | 22 | 34235;
}

function getTag(event: NostrEvent, name: string): string | undefined {
  return event.tags.find(([t]) => t === name)?.[1];
}

function getImeta(event: NostrEvent): Record<string, string> {
  const imetaTag = event.tags.find(([t]) => t === 'imeta');
  if (!imetaTag) return {};
  const result: Record<string, string> = {};
  for (let i = 1; i < imetaTag.length; i++) {
    const parts = imetaTag[i].split(' ');
    if (parts.length >= 2) {
      result[parts[0]] = parts.slice(1).join(' ');
    }
  }
  return result;
}

export function extractVideoUrl(event: NostrEvent): string | undefined {
  const imeta = getImeta(event);
  if (imeta.url) return imeta.url;
  // fallback: look for url tag
  return getTag(event, 'url');
}

export function extractThumbnail(event: NostrEvent): string | undefined {
  const imeta = getImeta(event);
  if (imeta.image) return imeta.image;
  return getTag(event, 'thumb') ?? getTag(event, 'image');
}

export function extractTitle(event: NostrEvent): string {
  return getTag(event, 'title') ?? 'Untitled Video';
}

export function extractDuration(event: NostrEvent): number | undefined {
  const imeta = getImeta(event);
  if (imeta.duration) return parseFloat(imeta.duration);
  const dur = getTag(event, 'duration');
  if (dur) return parseFloat(dur);
  return undefined;
}

export function extractTags(event: NostrEvent): string[] {
  return event.tags.filter(([t]) => t === 't').map(([, v]) => v).filter(Boolean);
}

/**
 * Returns true if the event is marked as sensitive/NSFW via any of the
 * standard Nostr signals:
 *  - NIP-36 `content-warning` tag
 *  - `["t", "nsfw"]` hashtag (common convention)
 *  - `["l", "nsfw"]` label
 *  - `["l", "NS-*", ...]` social.nos.ontology nudity/sex labels
 */
export function extractIsNsfw(event: NostrEvent): boolean {
  for (const tag of event.tags) {
    const [name, value = ''] = tag;
    if (name === 'content-warning') return true;
    if (name === 't' && value.toLowerCase() === 'nsfw') return true;
    if (name === 'l' && value.toLowerCase() === 'nsfw') return true;
    if (name === 'l' && /^NS-/i.test(value)) return true;
  }
  return false;
}

export function isYouTubeVideoEvent(event: NostrEvent): boolean {
  const videoUrl = extractVideoUrl(event);
  return !!videoUrl && isYouTubeUrl(videoUrl);
}

export function isValidVideoEvent(event: NostrEvent): boolean {
  const videoUrl = extractVideoUrl(event);
  if (!videoUrl) return false;
  // Accept both direct video file URLs and YouTube bridge URLs
  const title = extractTitle(event);
  return title.length > 0;
}

/**
 * Deduplicate a list of video events so only one instance of each video appears.
 *
 * Two deduplication passes are applied in order:
 *  1. Exact event-id duplicates (same event from multiple relays).
 *  2. Addressable-event coordinates (pubkey + kind + d-tag) for kinds 34235/34236:
 *     only the event with the highest `created_at` is kept, matching relay behaviour.
 */
export function deduplicateVideos(events: NostrEvent[]): NostrEvent[] {
  // Pass 1: dedupe by event id
  const byId = new Map<string, NostrEvent>();
  for (const event of events) {
    byId.set(event.id, event);
  }

  // Pass 2: for addressable kinds, dedupe by coordinate (pubkey:kind:d)
  const ADDRESSABLE_KINDS: number[] = [34235];
  const byCoord = new Map<string, NostrEvent>();
  const regular: NostrEvent[] = [];

  for (const event of byId.values()) {
    if (ADDRESSABLE_KINDS.includes(event.kind)) {
      const d = event.tags.find(([t]) => t === 'd')?.[1] ?? '';
      const coord = `${event.pubkey}:${event.kind}:${d}`;
      const existing = byCoord.get(coord);
      if (!existing || event.created_at > existing.created_at) {
        byCoord.set(coord, event);
      }
    } else {
      regular.push(event);
    }
  }

  return [...regular, ...byCoord.values()];
}

export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function formatTimeAgo(timestamp: number): string {
  const diff = Date.now() / 1000 - timestamp;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  if (diff < 2592000) return `${Math.floor(diff / 604800)}w ago`;
  if (diff < 31536000) return `${Math.floor(diff / 2592000)}mo ago`;
  return `${Math.floor(diff / 31536000)}y ago`;
}

export function useVideoFeed(tag?: string) {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['video-feed', tag ?? 'all'],
    queryFn: async (ctx) => {
      // kind 34236 (divine/addressable short videos) intentionally excluded
      const filter = tag
        ? { kinds: [21, 22, 34235], '#t': [tag], limit: 30 }
        : { kinds: [21, 22, 34235], limit: 30 };
      const events = await nostr.query([filter], { signal: ctx.signal });
      return deduplicateVideos(events.filter(isValidVideoEvent)) as VideoEvent[];
    },
    staleTime: 60000,
  });
}

export function useVideosByAuthor(pubkey: string) {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['video-feed-author', pubkey],
    queryFn: async (ctx) => {
      // kind 34236 (divine/addressable short videos) intentionally excluded
      const events = await nostr.query(
        [{ kinds: [21, 22, 34235], authors: [pubkey], limit: 30 }],
        { signal: ctx.signal },
      );
      return deduplicateVideos(events.filter(isValidVideoEvent)) as VideoEvent[];
    },
    staleTime: 60000,
    enabled: !!pubkey,
  });
}

/**
 * Returns short-form videos from two sources, merged and deduplicated:
 *  1. Native short-form kind: 22 (Short-form Portrait Video)
 *  2. Any kind 21/22/34235 tagged with #shorts
 *
 * kind 34236 (divine/addressable short videos) is intentionally excluded.
 */
export function useShortsFeed() {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['shorts-feed'],
    queryFn: async (ctx) => {
      const signal = ctx.signal;
      // Fetch native short-form kind AND #shorts-tagged videos in parallel
      // kind 34236 intentionally excluded
      const [nativeEvents, taggedEvents] = await Promise.all([
        nostr.query(
          [{ kinds: [22], limit: 30 }],
          { signal },
        ),
        nostr.query(
          [{ kinds: [21, 22, 34235], '#t': ['shorts'], limit: 30 }],
          { signal },
        ),
      ]);

      const combined = [...nativeEvents, ...taggedEvents];
      return deduplicateVideos(combined.filter(isValidVideoEvent)) as VideoEvent[];
    },
    staleTime: 60000,
  });
}

export function useVideoEvent(eventId: string) {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['video-event', eventId],
    queryFn: async (ctx) => {
      const [event] = await nostr.query(
        [{ ids: [eventId], limit: 1 }],
        { signal: ctx.signal },
      );
      return (event ?? null) as VideoEvent | null;
    },
    staleTime: 300000,
    enabled: !!eventId,
  });
}
